import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

interface Props {
  value: string;      // HTML from rtfToHtml
  onChange: (html: string) => void;
  disabled?: boolean;
}

const BTN =
  'px-2 py-1 text-sm font-medium rounded hover:bg-gray-200 transition-colors';
const BTN_ACTIVE = 'bg-gray-200 ring-1 ring-gray-400';

export function RtfEditor({ value, onChange, disabled }: Props) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. form reset)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value ?? '<p></p>', { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className={`border rounded-lg overflow-hidden ${disabled ? 'opacity-60 bg-gray-50' : 'border-gray-300'}`}>
      {!disabled && (
        <div className="flex flex-wrap gap-1 p-2 border-b border-gray-100 bg-gray-50">
          <button
            type="button"
            title="Bold"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}
            className={`${BTN} ${editor?.isActive('bold') ? BTN_ACTIVE : ''}`}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            title="Italic"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}
            className={`${BTN} ${editor?.isActive('italic') ? BTN_ACTIVE : ''}`}
          >
            <em>I</em>
          </button>
          <button
            type="button"
            title="Strike"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleStrike().run(); }}
            className={`${BTN} ${editor?.isActive('strike') ? BTN_ACTIVE : ''}`}
          >
            <s>S</s>
          </button>
          <div className="w-px bg-gray-300 self-stretch mx-1" />
          <button
            type="button"
            title="Bullet list"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBulletList().run(); }}
            className={`${BTN} ${editor?.isActive('bulletList') ? BTN_ACTIVE : ''}`}
          >
            &#8226; List
          </button>
          <button
            type="button"
            title="Ordered list"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleOrderedList().run(); }}
            className={`${BTN} ${editor?.isActive('orderedList') ? BTN_ACTIVE : ''}`}
          >
            1. List
          </button>
          <div className="w-px bg-gray-300 self-stretch mx-1" />
          <button
            type="button"
            title="Undo"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().undo().run(); }}
            className={BTN}
            disabled={!editor?.can().undo()}
          >
            ↩
          </button>
          <button
            type="button"
            title="Redo"
            onMouseDown={e => { e.preventDefault(); editor?.chain().focus().redo().run(); }}
            className={BTN}
            disabled={!editor?.can().redo()}
          >
            ↪
          </button>
        </div>
      )}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-3 min-h-[120px] focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[100px]"
      />
    </div>
  );
}
